import { useScreenHelp } from "./ScreenHelp";
// src/CqcInspectorView.js
// Inspector view — a single place to see compliance progress across every
// client organisation and every site, read-only. Gated to platform admins
// both here (client-side, same convention as SiteManagement.js checking for
// the "Pharmace all" organisation) and server-side (requirePlatformAdmin in
// cqc.js) — the client-side check is a UX convenience only, never the real
// security boundary.
//
// Three-pane layout mirrors SiteManagement.js: organisations on the left,
// that organisation's sites in the middle, the selected site's full
// compliance pack on the right.

import React, { useEffect, useState } from "react";
import CqcWorkspace from "./CqcWorkspace";
import { cqcApi } from "./cqcApi";
import {countryOf} from "./cqcFrameworks";
import { deriveSector } from "./cqcTaxonomy";
import { pctBg } from "./cqcColors";

const S = {
  wrap: { fontFamily: "system-ui, sans-serif", display: "flex", flexDirection: "column",
          height: "calc(100vh - 130px)", minHeight: 480 },
  header: { display: "flex", alignItems: "center", gap: 10, padding: "0 0 10px",
            borderBottom: "1px solid #F0F0F0", flexShrink: 0 },
  h1: { fontSize: 20, fontWeight: 700, margin: 0 },
  sub: { fontSize: 12, color: "#5D5D5D" },
  body: { flex: 1, display: "grid", gridTemplateColumns: "260px 260px 1fr", gap: 12,
          paddingTop: 12, overflow: "hidden", minHeight: 0 },
  panel: { background: "#fff", border: "1px solid #F0F0F0", borderRadius: 6,
           display: "flex", flexDirection: "column", overflow: "hidden" },
  panelHead: { padding: "10px 12px", borderBottom: "1px solid #F0F0F0", fontSize: 13, fontWeight: 700,
               color: "#434343", background: "#FAFAFA", flexShrink: 0 },
  panelBody: { flex: 1, overflow: "auto" },
  orgRow: (active) => ({ padding: "9px 12px", fontSize: 13, cursor: "pointer",
                          borderBottom: "1px solid #F5F5F5", background: active ? "#E6F7FF" : "transparent",
                          fontWeight: active ? 700 : 500 }),
  siteRow: (active) => ({ padding: "9px 12px", fontSize: 13, cursor: "pointer",
                           borderBottom: "1px solid #F5F5F5", background: active ? "#E6F7FF" : "transparent",
                           fontWeight: active ? 700 : 500 }),
  detailPad: { padding: 16 },
  kqRow: { display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: "1px solid #F5F5F5" },
  kqLabel: { fontSize: 13, fontWeight: 600, flex: 1 },
  pctChip: (pct) => ({ padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: pctBg(pct) }),
  empty: { fontSize: 13, color: "#8C8C8C", padding: 30, textAlign: "center" },
};

export default function CqcInspectorView({ isPlatformAdmin }) {
  const [orgs, setOrgs] = useState([]);
  const [activeOrg, setActiveOrg] = useState(null);
  const [orgSites, setOrgSites] = useState([]);
  const [activeSiteId, setActiveSiteId] = useState(null);
  const [pack, setPack] = useState(null);
  const [error, setError] = useState(null);

  useScreenHelp({ title: "Care compliance inspector", blurb: "Read-only access to care evidence within your permitted organisation scope.", can: ["Select an organisation, then a location to inspect its pack.", "No records need to be changed on this screen."], related: ["care-inspector"] }, 15);
  useEffect(() => {
    if (isPlatformAdmin === false) return;
    cqcApi.inspectorOrganisations().then((d) => setOrgs(d.organisations || [])).catch((e) => setError(e.message));
  }, [isPlatformAdmin]);

  useEffect(() => {
    if (!activeOrg) { setOrgSites([]); return; }
    let active = true; setOrgSites([]); setError(null);
    cqcApi.inspectorSites(activeOrg).then((d) => { if (active) setOrgSites(d.sites || []); }).catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [activeOrg]);

  useEffect(() => {
    if (!activeSiteId) { setPack(null); return; }
    let active = true; setPack(null); setError(null);
    cqcApi.inspectorSitePack(activeSiteId).then(d => { if (active) setPack(d); }).catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [activeSiteId]);

  if (isPlatformAdmin === false) {
    return <div style={S.wrap}><div style={S.empty}>Inspector view is restricted to platform administrators.</div></div>;
  }

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <h1 style={S.h1}>Care compliance inspector view</h1>
          <div style={S.sub}>Every client organisation's sites and compliance progress, in one place.</div>
        </div>
      </div>
      {error && <div style={{ color: "#A8071A", fontSize: 12.5, padding: "8px 0" }}>{error}</div>}
      <div style={S.body}>
        <div style={S.panel}>
          <div style={S.panelHead}>Organisations ({orgs.length})</div>
          <div style={S.panelBody}>
            {orgs.map((o) => (
              <div key={o.organisationName} style={S.orgRow(o.organisationName === activeOrg)}
                   onClick={() => { setActiveOrg(o.organisationName); setActiveSiteId(null); setPack(null); }}>
                {o.organisationName} <span style={{ color: "#8C8C8C" }}>· {o.siteCount}</span>
              </div>
            ))}
            {orgs.length === 0 && <div style={S.empty}>No organisations have connected sites yet.</div>}
          </div>
        </div>

        <div style={S.panel}>
          <div style={S.panelHead}>{activeOrg ? `Sites — ${activeOrg}` : "Sites"}</div>
          <div style={S.panelBody}>
            {orgSites.map((s) => (
              <div key={s._id} style={S.siteRow(s._id === activeSiteId)} onClick={() => setActiveSiteId(s._id)}>
                {s.name}
                <div style={{ fontSize: 11, color: "#8C8C8C", fontWeight: 400 }}>{countryOf(s)||"Country unresolved"} · {deriveSector(s).label}</div>
              </div>
            ))}
            {activeOrg && orgSites.length === 0 && <div style={S.empty}>No sites for this organisation.</div>}
          </div>
        </div>

        <div style={S.panel}>
          <div style={S.panelHead}>{pack ? pack.site.name : "Select a site"}</div>
          <div style={{ ...S.panelBody, ...S.detailPad }}>
            {!pack && <div style={S.empty}>Pick an organisation, then a site, to view its compliance pack.</div>}
            {pack && <CqcWorkspace key={pack.site._id} pack={pack} initialSiteId={pack.site._id} readOnly />}
          </div>
        </div>
      </div>
    </div>
  );
}
